import { AfterViewInit, Component, Input, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AssetModel } from '../../models/asset.model';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { UtilsService } from '../../shared/utils.service';
import { tap } from 'rxjs/operators';
import { ApiDataSource } from '../../utils/api-data-source';
import {
  AssetMaintenanceRecordsFilterComponent
} from './asset-maintenance-records-filter/asset-maintenance-records-filter.component';
import { AssetMaintenanceRecordModel } from '../../models/asset-maintenance-record.model';
import {
  AssetMaintenanceRecordsEditComponent
} from './asset-maintenance-records-edit/asset-maintenance-records-edit.component';
import { MatDialog } from '@angular/material/dialog';
import {
  AssetMaintenanceRecordsViewComponent
} from './asset-maintenance-records-view/asset-maintenance-records-view.component';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { AssetMaintenanceRecordsService } from './asset-maintenance-records.service';
import {
  AssetMaintenanceRecordsFilters
} from './asset-maintenance-records-filter/asset-maintenance-records-filter.interface';
import { RBACService } from '../../shared/rbac.service';
import { assetUserCanAccess } from '../assets-permissions';

@Component({
  selector: 'app-asset-maintenance-records',
  templateUrl: './asset-maintenance-records.component.html',
  styleUrls: ['./asset-maintenance-records.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetMaintenanceRecordsComponent implements OnInit, AfterViewInit {

  @Input('asset_id') asset_id: number;
  @Input('asset') asset: AssetModel;

  dataSource: AssetMaintenanceRecordsDataSource = new AssetMaintenanceRecordsDataSource(this.app, this.api);

  // Built dynamically in ngOnInit(). The checkbox column only drives Archive Selected and Restore
  // Selected, so it is added only for a user who can delete (see canDelete below).
  listColumns: string[] = [];

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  readonly MAX_VISIBLE_ASSIGNED_USERS = 3;

  // RBAC permission flags: resolved once in ngOnInit(). Maintenance records belong to an asset, so
  // they are governed by the asset permissions, evaluated across both the account level and the site
  // level asset perspectives because this tab is reached from both.
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public renderer: Renderer2,
    public assetMaintenanceRecordsService: AssetMaintenanceRecordsService,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    this.canCreate = assetUserCanAccess(this.rbac, RBACService.RBAC_A.A_CREATE);
    this.canView = assetUserCanAccess(this.rbac, RBACService.RBAC_A.A_VIEW);
    this.canEdit = assetUserCanAccess(this.rbac, RBACService.RBAC_A.A_EDIT);
    this.canDelete = assetUserCanAccess(this.rbac, RBACService.RBAC_A.A_DELETE);

    // Build the columns dynamically. Selection only drives archive and restore here (there is no
    // export), so the checkbox column is only useful to a user who can delete.
    this.listColumns = [];
    if (this.canDelete) {
      this.listColumns.push('select');
    }
    this.listColumns.push(
      'id',
      'title',
      'type',
      'status',
      'priority',
      'due_at',
      'users',
      'date_created',
      'actions'
    );

    // Only load the list when the user may view it.
    if (this.canView) {
      this.dataSource.asset_id = this.asset_id;
      this.dataSource.getData();
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter) => {
      this.dataSource.order_by = sorter['active'];
      this.dataSource.order = sorter['direction'];

      this.dataSource.getData();
    })).subscribe();
  }

  /**
   * Create a new Asset Maintenance Record.
   *
   * @return {void}
   */
  onAdd(): void {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create maintenance records.');
      return;
    }

    this.openEditDialog(new AssetMaintenanceRecordModel());
  }

  /**
   * Update the existing Asset Maintenance Record.
   *
   * @param {AssetMaintenanceRecordModel} asset_maintenance_record The asset maintenance record to be edited.
   * @return {void}
   */
  onEdit(asset_maintenance_record: AssetMaintenanceRecordModel): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit maintenance records.');
      return;
    }

    this.openEditDialog(asset_maintenance_record);
  }

  /**
   * Opens the create/edit dialog. Shared by onAdd() and onEdit(), each of which checks its own
   * permission first, so a create only user is never blocked by the edit check.
   *
   * @param {AssetMaintenanceRecordModel} asset_maintenance_record The asset maintenance record to be edited.
   * @return {void}
   */
  private openEditDialog(asset_maintenance_record: AssetMaintenanceRecordModel): void {
    this.dialog
      .open(AssetMaintenanceRecordsEditComponent, {
        width: '90%',
        data: {
          asset_id: this.asset_id,
          asset_maintenance_record_id: asset_maintenance_record.id
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed, as long as the user may view it.
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  /**
   * Display the Asset Maintenance Record data.
   *
   * @param {number} id - The unique identifier of the asset maintenance record.
   * @return {void}
   */
  onView(id: number): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view maintenance records.');
      return;
    }

    this.utils.showComponentDialog(
      AssetMaintenanceRecordsViewComponent,
      {
        asset_id: this.asset_id,
        asset_maintenance_record_id: id
      },
      { width: '900px' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        if (this.canView) {
          this.dataSource.getData(true);
        }
      }
    );
  }

  /**
   * Archives selected Asset Maintenance Records.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive maintenance records.');
      return;
    }

    this.utils.showModal(
      'Archive Asset Maintenance Record(s)',
      `Are you sure you want to archive ${this.dataSource.selection.selected.length} asset maintenance record(s)?`,
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'Asset Maintenance Record(s) have been archived.');
      }
    );
  }

  /**
   * Archives the specified Asset Maintenance Record.
   *
   * @param {AssetMaintenanceRecordModel} asset_maintenance_record - The asset maintenance record to be archived.
   * @return {void}
   */
  onRemove(asset_maintenance_record: AssetMaintenanceRecordModel): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive maintenance records.');
      return;
    }

    this.utils.showModal(
      'Archive Asset Maintenance Record',
      `Are you sure you want to archive "${asset_maintenance_record.title}"?`,
      () => {
        this.remove(asset_maintenance_record.id, 'Asset Maintenance Record has been archived.');
      }
    );
  }

  /**
   * Archives Asset Maintenance Records with the specified IDs.
   *
   * @param {any} ids - IDs of asset maintenance records to be removed.
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/assets/${this.asset_id}/maintenance-records/${ids}`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore Asset Maintenance Record.
   *
   * @param {number} id - The ID of the asset maintenance record to be restored.
   * @return {void}
   */
  onRestore(id: number): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore maintenance records.');
      return;
    }

    this.utils.showModal(
      'Restore Asset Maintenance Record',
      'Are you sure you want to restore this asset maintenance record?',
      () => {
        this.restore(id, 'The Asset Maintenance Record was restored.');
      }
    );
  }

  /**
   * Restore selected Asset Maintenance Records.
   *
   * @return {void}
   */
  onRestoreSelected(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore maintenance records.');
      return;
    }

    this.utils.showModal(
      'Restore Selected Asset Maintenance Record(s)',
      'Are you sure you want to restore the selected asset maintenance records?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The selected Asset Maintenance Record(s) were restored.');
      }
    );
  }

  /**
   * Restores Asset Maintenance Records with the specified IDs.
   *
   * @param {any} ids - IDs of asset maintenance records to be restored.
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v2/assets/${this.asset_id}/maintenance-records/${ids}/restore`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the filter dialog and apply the selected filters.
   *
   * @return {void}
   */
  onFilter(): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view maintenance records.');
      return;
    }

    this.utils.showComponentDialog(AssetMaintenanceRecordsFilterComponent, {
        asset_id: this.asset_id,
        archived: this.dataSource.filters.archived,
        schedule_ids: this.dataSource.filters.schedule_ids,
        contractor_ids: this.dataSource.filters.contractor_ids,
        vendor_ids: this.dataSource.filters.vendor_ids,
        user_ids: this.dataSource.filters.user_ids,
        status: this.dataSource.filters.status,
        type: this.dataSource.filters.type,
        priority: this.dataSource.filters.priority,
        date_type: this.dataSource.filters.date_type,
        date_range: this.dataSource.filters.date_range
      }, {
        width: '1024px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.filters.archived = results.archived ?? this.dataSource.filters.archived;
        this.dataSource.filters.schedule_ids = results.schedule_ids ?? this.dataSource.filters.schedule_ids;
        this.dataSource.filters.contractor_ids = results.contractor_ids ?? this.dataSource.filters.contractor_ids;
        this.dataSource.filters.vendor_ids = results.vendor_ids ?? this.dataSource.filters.vendor_ids;
        this.dataSource.filters.user_ids = results.user_ids ?? this.dataSource.filters.user_ids;
        this.dataSource.filters.status = results.status ?? this.dataSource.filters.status;
        this.dataSource.filters.type = results.type ?? this.dataSource.filters.type;
        this.dataSource.filters.priority = results.priority ?? this.dataSource.filters.priority;
        this.dataSource.filters.date_type = results.date_type ?? this.dataSource.filters.date_type;
        this.dataSource.filters.date_range = results.date_range ?? this.dataSource.filters.date_range;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Retrieves a tooltip string containing names of remaining users beyond a maximum visible limit.
   *
   * @param {any[]} users - An array of user objects.
   * @returns {string} A string containing the names of users beyond the maximum visible count.
   */
  getRemainingUsersTooltip(users: any[]): string {
    if (!users || users.length <= this.MAX_VISIBLE_ASSIGNED_USERS) {
      return '';
    }

    return users
      .slice(this.MAX_VISIBLE_ASSIGNED_USERS)
      .map(user => user.contact_person)
      .join(', ');
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   * @return {void}
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
      }
    );
  }

}

export class AssetMaintenanceRecordsDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  asset_id: number;

  filters: AssetMaintenanceRecordsFilters = {
    archived: 'false',
    schedule_ids: [],
    contractor_ids: [],
    vendor_ids: [],
    user_ids: [],
    status: 'all',
    type: 'all',
    priority: 'all',
    date_type: 'due_at',
    date_range: []
  };

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/assets/${this.asset_id}/maintenance-records`, resetOffset, {
      archived: this.filters.archived,
      schedule_ids: this.filters.schedule_ids.join(','),
      contractor_ids: this.filters.contractor_ids.join(','),
      vendor_ids: this.filters.vendor_ids.join(','),
      user_ids: this.filters.user_ids.join(',') ,
      status: this.filters.status ?? 'all',
      type: this.filters.type ?? 'all',
      priority: this.filters.priority ?? 'all',
      date_type: this.filters.date_type,
      date_range: this.filters.date_range.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(',')
    });
  }
}
